import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  FileText,
  Clock,
  CheckCircle,
  Send,
  TrendingUp,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Calendar,
  ArrowRight,
  Sparkles,
  RotateCcw,
  Building2,
  Plane,
  Heart as HeartIcon
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// Brand icons and colors
const brandConfig = {
  "MAAVI": { icon: Building2, color: "bg-blue-500", textColor: "text-blue-500" },
  "TripMate AI": { icon: Plane, color: "bg-emerald-500", textColor: "text-emerald-500" },
  "Solo&Co": { icon: HeartIcon, color: "bg-pink-500", textColor: "text-pink-500" }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("all");
  const [activeBrand, setActiveBrand] = useState(null);

  useEffect(() => {
    fetchBrands();
    fetchActiveBrand();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedBrandId]);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/api/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchActiveBrand = async () => {
    try {
      const response = await axios.get(`${API}/api/brands/active`);
      setActiveBrand(response.data);
    } catch (error) {
      console.error("Error fetching active brand:", error);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = selectedBrandId !== "all" ? `?brand_id=${selectedBrandId}` : "";
      const response = await axios.get(`${API}/api/dashboard/stats${params}`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCounters = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser tous les compteurs ? Cette action est irréversible.")) {
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/api/analytics/reset`);
      toast.success("Compteurs réinitialisés avec succès");
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Posts",
      value: stats?.posts?.total || 0,
      icon: FileText,
      color: "text-blue-500",
      bg: "bg-blue-500/10"
    },
    {
      title: "En attente",
      value: stats?.posts?.pending_validation || stats?.posts?.pending || 0,
      icon: Clock,
      color: "text-yellow-500",
      bg: "bg-yellow-500/10"
    },
    {
      title: "Programmés",
      value: stats?.posts?.scheduled || 0,
      icon: CheckCircle,
      color: "text-green-500",
      bg: "bg-green-500/10"
    },
    {
      title: "Publiés",
      value: stats?.posts?.published || 0,
      icon: Send,
      color: "text-purple-500",
      bg: "bg-purple-500/10"
    }
  ];

  const engagementStats = [
    { label: "Vues", value: stats?.analytics?.totals?.total_impressions || stats?.engagement?.views || 0, icon: Eye },
    { label: "J'aime", value: stats?.analytics?.totals?.total_likes || stats?.engagement?.likes || 0, icon: Heart },
    { label: "Commentaires", value: stats?.analytics?.totals?.total_comments || stats?.engagement?.comments || 0, icon: MessageCircle },
    { label: "Partages", value: stats?.analytics?.totals?.total_shares || stats?.engagement?.shares || 0, icon: Share2 }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header with Brand Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">
            Vue d&apos;ensemble de votre activité marketing
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Brand Filter */}
          <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
            <SelectTrigger className="w-48" data-testid="brand-filter">
              <SelectValue placeholder="Toutes les marques" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les marques</SelectItem>
              {brands.map((brand) => {
                const config = brandConfig[brand.name] || brandConfig["MAAVI"];
                const BrandIcon = config.icon;
                return (
                  <SelectItem key={brand.id} value={brand.id}>
                    <div className="flex items-center gap-2">
                      <BrandIcon className={`w-4 h-4 ${config.textColor}`} />
                      {brand.name}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button onClick={() => navigate("/create")} className="gap-2">
            <Sparkles className="w-4 h-4" />
            Créer du contenu
          </Button>
        </div>
      </div>

      {/* Active Brand Indicator */}
      {activeBrand && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border">
          {(() => {
            const config = brandConfig[activeBrand.name] || brandConfig["MAAVI"];
            const BrandIcon = config.icon;
            return (
              <>
                <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                  <BrandIcon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{activeBrand.name}</p>
                  <p className="text-xs text-muted-foreground">Marque active pour la création de contenu</p>
                </div>
                <Badge variant="outline" className={config.textColor}>
                  {selectedBrandId === "all" ? "Affichage: Toutes" : "Filtré"}
                </Badge>
              </>
            );
          })()}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Performance (30 derniers jours)
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetCounters}
              disabled={resetting}
              className="text-muted-foreground hover:text-destructive"
              data-testid="reset-counters-btn"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
              Réinitialiser
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {engagementStats.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-muted/30 rounded-lg">
                  <stat.icon className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">{stat.value.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => navigate("/validation")}
            >
              File de validation
              <Badge variant="secondary">{stats?.posts?.pending || 0}</Badge>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => navigate("/calendar")}
            >
              Calendrier
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => navigate("/publications")}
            >
              Publications
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => navigate("/analytics")}
            >
              Analytics détaillés
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Activité récente</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_posts?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_posts.map((post, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium line-clamp-1">{post.content?.substring(0, 100)}...</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {post.network} • {new Date(post.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <Badge
                    variant={
                      post.status === "published"
                        ? "default"
                        : post.status === "approved"
                        ? "secondary"
                        : "outline"
                    }
                  >
                    {post.status === "published"
                      ? "Publié"
                      : post.status === "approved"
                      ? "Approuvé"
                      : "En attente"}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Aucune activité récente
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
